import type { ActivityItem, GoalStatus } from '../lib/types';
import { fmtAgo } from '../lib/format';

/** One goal's week, as a pill: green when met, rose when missed, plain when there is not enough data. */
export function GoalPill({ goal }: { goal: GoalStatus }) {
  const tone = goal.met === true ? 'badge-green' : goal.met === false ? 'badge-red' : 'badge-soft';
  const mark = goal.met === true ? '✓ ' : goal.met === false ? '✕ ' : '';
  return (
    <span className={`badge ${tone}`} title={`${goal.label}: target ${goal.target}`}>
      {mark}
      {goal.actual}
    </span>
  );
}

/** How many of a patient's goals they are keeping this week. */
export function GoalsMet({ met, total }: { met: number; total: number }) {
  if (total === 0) return <span className="hint">no goals set</span>;
  const tone = met === total ? 'badge-green' : met === 0 ? 'badge-red' : 'badge-gold';
  return <span className={`badge ${tone}`}>{met === total ? `All ${total} goals met` : `${met} of ${total} goals met`}</span>;
}

export function GoalList({ goals }: { goals: GoalStatus[] }) {
  if (goals.length === 0) return <p className="hint">No care goals set yet.</p>;
  return (
    <ul className="goal-list">
      {goals.map((g) => (
        <li key={g.kind}>
          <span className="row-main">
            <strong>{g.label}</strong>
            <span className="hint">Target {g.target}</span>
          </span>
          <GoalPill goal={g} />
        </li>
      ))}
    </ul>
  );
}

export type FeedItem = ActivityItem & { name?: string; href?: string };

/** What a patient (or every patient) did lately, newest first. */
export function ActivityList({ items, empty = 'Nothing logged yet.' }: { items: FeedItem[]; empty?: string }) {
  if (items.length === 0) return <p className="hint">{empty}</p>;
  return (
    <ul className="activity">
      {items.map((a, i) => (
        <li key={`${a.at}-${i}`}>
          <span className="pip" aria-hidden="true" />
          <span>
            {a.name ? (
              a.href ? (
                <a className="who" href={a.href}>
                  {a.name}
                </a>
              ) : (
                <span className="who">{a.name}</span>
              )
            ) : null}
            {a.name ? ' · ' : ''}
            {a.text}
          </span>
          <span className="when">{fmtAgo(a.at)}</span>
        </li>
      ))}
    </ul>
  );
}
